import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Digite seu email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (error) {
      // Always show neutral message for security
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-dark-card rounded-3xl p-8 border border-dark-border shadow-dark-medium text-center">
            <div className="w-16 h-16 bg-cyan/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-cyan" />
            </div>
            
            <h1 className="font-fraunces text-2xl font-semibold text-dark-text mb-3">
              Verifique seu e-mail
            </h1>
            
            <p className="text-dark-muted mb-6">
              Se existir uma conta com o e-mail <strong className="text-dark-text">{email}</strong>, 
              enviaremos um link para redefinir sua senha.
            </p>
            
            <p className="text-dark-muted text-sm mb-6">
              O link expira em 1 hora. Verifique também sua pasta de spam.
            </p>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-vocalira-gradient text-white hover:opacity-90 rounded-full py-6"
              >
                Voltar ao Login
              </Button>
              
              <button
                onClick={() => {
                  setSubmitted(false);
                  setEmail('');
                }}
                className="text-dark-muted hover:text-vocalira text-sm transition-colors"
              >
                Tentar outro e-mail
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-dark-card rounded-3xl p-8 border border-dark-border shadow-dark-medium">
          {/* Back Button */}
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-dark-muted hover:text-dark-text mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao login
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-vocalira/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-vocalira" />
            </div>
            <h1 className="font-fraunces text-2xl font-semibold text-dark-text mb-2">
              Esqueceu sua senha?
            </h1>
            <p className="text-dark-muted">
              Sem problemas! Digite seu e-mail e enviaremos um link para criar uma nova senha.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-dark-text font-medium">
                E-mail da conta
              </Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-muted" />
                <Input
                  id="email"
                  type="email"
                  data-testid="forgot-password-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-dark-elevated border-dark-border text-dark-text rounded-xl pl-12 pr-4 py-3 h-12 focus:ring-2 focus:ring-vocalira/30 focus:border-vocalira"
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="forgot-password-submit"
              disabled={loading}
              className="w-full bg-vocalira-gradient text-white hover:opacity-90 rounded-full py-6 font-medium shadow-glow"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar link de recuperação'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-dark-muted text-sm">
              Lembrou a senha?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-vocalira font-medium hover:text-vocalira-light transition-colors"
              >
                Fazer login
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
